import { Badge, type BadgeProps } from '@chakra-ui/react'

/**
 * Colour by what the role can do, so a member list separates at a glance:
 * `owner` and `admin` warm because they can change the org, `viewer` neutral
 * because it cannot, and `support_admin` blue because it is HiveMQ staff
 * rather than a customer — the one distinction that drives an action here
 * (only support users can be removed).
 *
 * `OrgUser.roles` is typed `string[]`, not an enum, so this is a partial
 * lookup with a neutral default: an unknown role from the backend renders
 * grey rather than crashing or vanishing.
 */
const ROLE_COLORS: Record<string, string> = {
  owner: 'purple',
  admin: 'orange',
  viewer: 'gray',
  support_admin: 'blue',
}

type Props = {
  /**
   * Named `roleName`, not `role`: a JSX prop called `role` reads as the ARIA
   * attribute to both linters and reviewers.
   */
  roleName: string
} & Omit<BadgeProps, 'colorPalette' | 'children'>

export function MemberRoleBadge({ roleName, ...badge }: Readonly<Props>) {
  return (
    <Badge
      variant="subtle"
      {...badge}
      colorPalette={ROLE_COLORS[roleName] ?? 'gray'}
      textTransform="capitalize"
    >
      {roleName.replaceAll('_', ' ')}
    </Badge>
  )
}
